import { useCallback, useEffect, useState } from 'react';
import { portfolioApi } from '../services/portfolioApi';
import { STUDENT_PORTFOLIO_STUBS, LEARNER_PORTFOLIO_STUB } from '../fixtures/portfolioStub';

// Backend V1 stub is live for GET /api/v1/parent/students/:id/portfolio.
const USE_STUB = false;

export function useStudentPortfolio(studentId) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Backend Phase 7: portfolio is real. This 404 branch now only fires in
  // the genuine "you're no longer linked to this student" case (e.g. link
  // was revoked in another tab). Surfacing it distinctly lets the UI show
  // a specific "not linked" message instead of a generic ErrorBlock.
  const [unavailable, setUnavailable] = useState(false);

  // Guards against a stale in-flight request (e.g. from a previous
  // studentId) resolving after a newer one has started and overwriting
  // fresher state with old data.
  const load = useCallback(async (ignore = { current: false }) => {
    if (!studentId) return;
    setLoading(true);
    setError(null);
    setUnavailable(false);
    try {
      let result;
      if (USE_STUB) {
        await new Promise((r) => setTimeout(r, 120));
        const known = STUDENT_PORTFOLIO_STUBS[studentId];
        result = known || {
          ...LEARNER_PORTFOLIO_STUB,
          studentId,
          displayName: 'Unknown student',
        };
      } else {
        result = await portfolioApi.getStudentPortfolio({ studentId });
      }
      if (!ignore.current) setData(result);
    } catch (e) {
      if (ignore.current) return;
      if (e?.status === 404) {
        setUnavailable(true);
      } else {
        setError(e?.message || 'Failed to load portfolio.');
      }
    } finally {
      if (!ignore.current) setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    const ignore = { current: false };
    load(ignore);
    return () => { ignore.current = true; };
  }, [load]);

  return { data, loading, error, unavailable, reload: load };
}
